import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useAuth } from "../contexts/AuthContext";
import { useCachedImage } from "../hooks/useCachedImage";

// Image URLs for caching
const YOGA_IMAGE_URL =
  "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=150&h=150&fit=crop&crop=center";
const GAMES_IMAGE_URL =
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=150&h=150&fit=crop&crop=center";
const SPORTS_IMAGE_URL =
  "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=150&h=150&fit=crop&crop=center";

// SVG components for activity-themed decorative elements
const ActivityIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 1v6m0 6v6m11-7h-6m-6 0H1"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const PeopleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    <path
      d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const LocationIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ConnectIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M7 17l9.2-9.2M17 17l-9.2-9.2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="7" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    <circle cx="17" cy="17" r="4" stroke="currentColor" strokeWidth="2" />
  </svg>
);

// Animation variants for floating effect
const floatVariant: Variants = {
  initial: { y: 0, rotate: 0 },
  animate: {
    y: [0, -12, 8, -12, 0],
    rotate: [0, 3, -2, 3, 0],
    transition: {
      duration: 20,
      ease: "easeInOut",
      repeat: Infinity,
      repeatType: "loop",
    },
  },
};

const WelcomePage: React.FC = () => {
  usePageTracking("Welcome", "WelcomePage");
  const navigate = useNavigate();

  const { isAuthenticated, isLoading, currentUser } = useAuth();

  // Cache welcome page images
  const yogaImage = useCachedImage(
    YOGA_IMAGE_URL,
    "yoga-activity",
    "welcome-yoga",
  );
  const gamesImage = useCachedImage(
    GAMES_IMAGE_URL,
    "games-activity",
    "welcome-games",
  );
  const sportsImage = useCachedImage(
    SPORTS_IMAGE_URL,
    "sports-activity",
    "welcome-sports",
  );

  // Handle image loading errors
  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    const img = e.currentTarget;
    const currentFallback = img.getAttribute("data-fallback-level") || "0";

    // Fallback to a placeholder if the image fails to load (only once)
    if (currentFallback === "0") {
      img.setAttribute("data-fallback-level", "1");
      img.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23E5E7EB'/%3E%3Ctext x='75' y='80' text-anchor='middle' fill='%239CA3AF' font-size='12'%3EActivity%3C/text%3E%3C/svg%3E`;
    }
    // Stop here - no more fallbacks to prevent infinite loops
  };

  if (!isLoading && currentUser && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className="h-full-safe bg-white text-gray-800 fixed inset-0 flex flex-col overflow-hidden"
      style={{ willChange: "auto" }}
    >
      {/* Top Section with graphics */}
      <main
        className="relative flex items-center justify-center flex-1 overflow-hidden"
        style={{ willChange: "auto" }}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Main Gradient Blob */}
          <motion.div
            className="absolute inset-0"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -20, 12, -20, 0],
              rotate: [0, 5, -3, 5, 0],
              scale: [1, 1.05, 0.98, 1.05, 1],
            }}
            transition={{
              duration: 15,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
            }}
          >
            <svg
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full"
            >
              <path
                fill="url(#gradient1)"
                d="M47.2,-57.8C59.8,-45.2,67.5,-28.8,71.1,-11.2C74.7,6.4,74.2,25.2,65.8,39.6C57.4,54,41.1,64,23.7,69.2C6.3,74.4,-12.2,74.8,-28.1,67.8C-44,60.8,-57.3,46.4,-64.1,29.7C-70.9,13,-71.2,-6,-64.7,-22.4C-58.2,-38.8,-44.9,-52.6,-29.7,-64.2C-14.5,-75.8,2.6,-85.2,20.3,-81.8C38,-78.4,56.3,-62.2,47.2,-57.8Z"
                transform="translate(100 100)"
              />
              <defs>
                <linearGradient
                  id="gradient1"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#8B5CF6" />
                  <stop offset="50%" stopColor="#06B6D4" />
                  <stop offset="100%" stopColor="#10B981" />
                </linearGradient>
              </defs>
            </svg>
          </motion.div>

          {/* Activity-themed floating elements */}
          <motion.div
            className="absolute top-[10%] left-[5%] text-purple-600"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -20, 15, -20, 0],
              rotate: [0, 8, -5, 8, 0],
              scale: [1, 1.1, 0.95, 1.1, 1],
            }}
            transition={{
              duration: 12,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 2,
            }}
          >
            <ActivityIcon />
          </motion.div>

          <motion.div
            className="absolute top-[25%] right-[5%] text-blue-600"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -25, 10, -25, 0],
              rotate: [0, -7, 5, -7, 0],
              scale: [1, 1.15, 0.9, 1.15, 1],
            }}
            transition={{
              duration: 14,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 1,
            }}
          >
            <PeopleIcon />
          </motion.div>

          <motion.div
            className="absolute bottom-[10%] right-[15%] text-green-600"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -18, 12, -18, 0],
              rotate: [0, 6, -4, 6, 0],
              scale: [1, 1.08, 0.96, 1.08, 1],
            }}
            transition={{
              duration: 13,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 3,
            }}
          >
            <LocationIcon />
          </motion.div>

          <motion.div
            className="absolute top-[15%] left-[15%] text-teal-600"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -22, 14, -22, 0],
              rotate: [0, -8, 6, -8, 0],
              scale: [1, 1.12, 0.92, 1.12, 1],
            }}
            transition={{
              duration: 16,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 0.5,
            }}
          >
            <ConnectIcon />
          </motion.div>

          {/* Activity images - people doing activities together */}
          <motion.div
            className="absolute top-[8%] right-[8%] w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full p-1 shadow-lg"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -24, 16, -24, 0],
              rotate: [0, 10, -8, 10, 0],
              scale: [1, 1.15, 0.88, 1.15, 1],
            }}
            transition={{
              duration: 11,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 1.5,
            }}
          >
            <img
              src={yogaImage}
              className="rounded-full w-full h-full object-cover"
              alt="People doing yoga together"
              onError={handleImageError}
            />
          </motion.div>

          <motion.div
            className="absolute bottom-[12%] left-[8%] w-20 h-20 bg-gradient-to-br from-green-100 to-blue-100 rounded-full p-1 shadow-lg"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -28, 18, -28, 0],
              rotate: [0, -9, 7, -9, 0],
              scale: [1, 1.18, 0.85, 1.18, 1],
            }}
            transition={{
              duration: 13,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 2.5,
            }}
          >
            <img
              src={gamesImage}
              className="rounded-full w-full h-full object-cover"
              alt="Friends playing board games"
              onError={handleImageError}
            />
          </motion.div>

          <motion.div
            className="absolute top-[35%] right-[5%] w-14 h-14 bg-gradient-to-br from-orange-100 to-pink-100 rounded-full p-1 shadow-lg"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -20, 14, -20, 0],
              rotate: [0, 12, -10, 12, 0],
              scale: [1, 1.2, 0.9, 1.2, 1],
            }}
            transition={{
              duration: 12,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 0.8,
            }}
          >
            <img
              src={sportsImage}
              className="rounded-full w-full h-full object-cover"
              alt="People playing pickleball together"
              onError={handleImageError}
            />
          </motion.div>

          {/* Activity tags */}
          <motion.div
            className="absolute top-[5%] right-[25%] bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg"
            initial={{ y: 0, rotate: 0 }}
            animate={{ y: [0, -12, 8, -12, 0], rotate: [0, 3, -2, 3, 0] }}
            transition={{
              duration: 20,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 1,
            }}
          >
            #Fitness
          </motion.div>

          <motion.div
            className="absolute bottom-[25%] right-[5%] bg-gradient-to-r from-green-500 to-teal-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg"
            initial={{ y: 0, rotate: 0 }}
            animate={{ y: [0, -12, 8, -12, 0], rotate: [0, 3, -2, 3, 0] }}
            transition={{
              duration: 20,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 9,
            }}
          >
            #Meetups
          </motion.div>

          <motion.div
            className="absolute top-[45%] left-[2%] bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg"
            initial={{ y: 0, rotate: 0 }}
            animate={{ y: [0, -12, 8, -12, 0], rotate: [0, 3, -2, 3, 0] }}
            transition={{
              duration: 20,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 11,
            }}
          >
            #Sports
          </motion.div>

          <motion.div
            className="absolute bottom-[45%] left-[50%] -translate-x-1/2 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg"
            initial={{ y: 0, rotate: 0 }}
            animate={{ y: [0, -12, 8, -12, 0], rotate: [0, 3, -2, 3, 0] }}
            transition={{
              duration: 20,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
              delay: 13,
            }}
          >
            #Social
          </motion.div>

          {/* Central message */}
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-5 py-2.5 rounded-full font-bold shadow-xl border-2 border-purple-200 text-sm"
            initial={{ y: 0, rotate: 0, scale: 1 }}
            animate={{
              y: [0, -16, 10, -16, 0],
              rotate: [0, 4, -3, 4, 0],
              scale: [1, 1.08, 0.96, 1.08, 1],
            }}
            transition={{
              duration: 10,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop",
            }}
          >
            Connect & <span className="text-purple-600">Explore</span>
          </motion.div>
        </div>
      </main>

      {/* Bottom Section */}
      <footer
        className="pt-100 px-6 pb-safe text-center flex flex-col justify-evenly flex-1 max-w-md mx-auto w-full"
        style={{ height: "auto" }}
      >
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 leading-tight">
          <span>
            Find Your Tribe.
            <br />
            <span className="text-purple-600">Discover Activities.</span>
          </span>
        </h1>

        <p className="text-gray-600 text-base max-w-sm mx-auto">
          Connect with like-minded people through shared activities.
        </p>

        {/* Feature highlights */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm sm:text-base">
          <div className="flex items-center space-x-2 text-gray-600">
            <LocationIcon className="w-4 h-4 text-purple-500" />
            <span>Find Activities</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600">
            <PeopleIcon className="w-4 h-4 text-blue-500" />
            <span>Meet People</span>
          </div>
        </div>

        <button
          onClick={() => navigate("/signup")}
          className="w-full max-w-sm mx-auto bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 rounded-full transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          Start Connecting
        </button>

        <p className="text-base sm:text-lg text-gray-500 -mt-2">
          Already have an account?{" "}
          <span
            onClick={() => navigate("/login")}
            className="font-bold text-purple-600 cursor-pointer hover:underline"
          >
            Sign In
          </span>
        </p>

        {/* Additional tagline */}
        <p className="text-sm sm:text-base text-gray-400 italic">
          "Life's better when you're doing things together"
        </p>
      </footer>
    </div>
  );
};

export default WelcomePage;
